import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRightLeft, Check, AlertCircle, X, RefreshCw, Filter, Clock, CheckCircle2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import {
  getTransferenciasInterLinea,
  getTransferenciasInterLineaPendientes,
  getResumenTransferenciasPorLinea,
  verificarTransferenciaInterLinea,
  anularVerificacionTransferenciaInterLinea,
} from '../services/api';
import { useEmpresa } from '../context/EmpresaContext';

const fmtMoney = (v) => `S/ ${Number(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNumber = (v) => Number(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d.length === 10 ? d + 'T00:00:00' : d);
  return dt.toLocaleDateString('es-PE');
};

const ESTADO_BADGE = {
  CONFIRMADO: 'bg-blue-100 text-blue-700 border-blue-300',
  REVERSADA: 'bg-orange-100 text-orange-700 border-orange-300',
  VERIFICADA: 'bg-emerald-100 text-emerald-700 border-emerald-300',
};

export default function TransferenciasInterLinea() {
  const { empresaActual } = useEmpresa();
  const [tab, setTab] = useState('pendientes');  // pendientes | todas | resumen
  const [pendientes, setPendientes] = useState([]);
  const [todas, setTodas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({
    estado: '',
    fecha_desde: '',
    fecha_hasta: '',
    incluir_no_verificadas: true,
  });

  const cargarPendientes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getTransferenciasInterLineaPendientes();
      setPendientes(r.data.items || []);
    } catch (e) {
      toast.error('Error al cargar pendientes');
    } finally {
      setLoading(false);
    }
  }, []);

  const cargarTodas = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.estado) params.estado = filtros.estado;
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      const r = await getTransferenciasInterLinea(params);
      setTodas(r.data.items || []);
    } catch (e) {
      toast.error('Error al cargar transferencias');
    } finally {
      setLoading(false);
    }
  }, [filtros.estado, filtros.fecha_desde, filtros.fecha_hasta]);

  const cargarResumen = useCallback(async () => {
    setLoading(true);
    try {
      const params = { incluir_no_verificadas: filtros.incluir_no_verificadas };
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      const r = await getResumenTransferenciasPorLinea(params);
      setResumen(r.data);
    } catch (e) {
      toast.error('Error al cargar resumen');
    } finally {
      setLoading(false);
    }
  }, [filtros.incluir_no_verificadas, filtros.fecha_desde, filtros.fecha_hasta]);

  useEffect(() => {
    if (!empresaActual) return;
    if (tab === 'pendientes') cargarPendientes();
    else if (tab === 'todas') cargarTodas();
    else if (tab === 'resumen') cargarResumen();
  }, [tab, empresaActual, cargarPendientes, cargarTodas, cargarResumen]);

  const handleVerificar = async (t) => {
    const obs = window.prompt(
      `Verificar transferencia ${t.codigo}?\n\n` +
      `${t.linea_origen_nombre} → ${t.linea_destino_nombre}\n` +
      `${fmtNumber(t.cantidad)} ${t.unidad_medida || ''} · ${fmtMoney(t.costo_total)}\n\n` +
      `Observaciones (opcional):`
    );
    if (obs === null) return;
    try {
      await verificarTransferenciaInterLinea(t.transferencia_prod_id, obs || '');
      toast.success('Transferencia verificada');
      if (tab === 'pendientes') cargarPendientes();
      else cargarTodas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al verificar');
    }
  };

  const handleAnularVerif = async (t) => {
    if (!window.confirm(`Anular verificación de ${t.codigo}?`)) return;
    try {
      await anularVerificacionTransferenciaInterLinea(t.transferencia_prod_id);
      toast.success('Verificación anulada');
      cargarTodas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al anular');
    }
  };

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ArrowRightLeft className="h-6 w-6" />
            Transferencias Inter-Línea
          </h1>
          <p className="text-sm text-gray-500">
            Vista contable de las transferencias de stock entre líneas de negocio (a costo)
          </p>
        </div>
        <button
          onClick={() => {
            if (tab === 'pendientes') cargarPendientes();
            else if (tab === 'todas') cargarTodas();
            else cargarResumen();
          }}
          className="px-3 py-1.5 text-xs border rounded hover:bg-gray-50 flex items-center gap-1"
          data-testid="btn-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b flex gap-1">
        {[
          { id: 'pendientes', label: `Pendientes${pendientes.length > 0 ? ` (${pendientes.length})` : ''}`, icon: Clock },
          { id: 'todas', label: 'Todas', icon: ArrowRightLeft },
          { id: 'resumen', label: 'Resumen por línea', icon: Filter },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 border-b-2 transition-colors ${
              tab === t.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon className="h-3.5 w-3.5" /> {t.label}
            {t.id === 'pendientes' && pendientes.length > 0 && (
              <span className="bg-amber-500 text-white rounded-full h-4 min-w-[16px] px-1 text-[10px] flex items-center justify-center">
                {pendientes.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* PENDIENTES */}
      {tab === 'pendientes' && (
        <div>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Cargando...</div>
          ) : pendientes.length === 0 ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-emerald-800">No hay transferencias pendientes de verificar</p>
              <p className="text-xs text-emerald-700 mt-1">Todas las transferencias están al día.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pendientes.map(t => (
                <div key={t.transferencia_prod_id} className="rounded-lg border bg-white p-4 flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-[300px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-xs font-semibold">{t.codigo}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${ESTADO_BADGE.CONFIRMADO}`}>
                        Pendiente verificación
                      </span>
                      <span className="text-xs text-gray-500">{fmtDate(t.fecha)}</span>
                    </div>
                    <div className="text-sm flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{t.linea_origen_nombre}</span>
                      <span className="text-gray-400">→</span>
                      <span className="font-medium">{t.linea_destino_nombre}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {t.item_origen_codigo} {t.item_origen_nombre}
                      {t.item_origen_codigo !== t.item_destino_codigo && (
                        <span className="ml-1 text-amber-700">→ {t.item_destino_codigo} {t.item_destino_nombre}</span>
                      )}
                    </div>
                    <div className="flex gap-4 mt-1 text-xs">
                      <span><strong>{fmtNumber(t.cantidad)}</strong> {t.unidad_medida}</span>
                      <span>Costo unit.: <strong>{fmtMoney(t.costo_unitario_promedio)}</strong></span>
                      <span className="text-blue-700">Total: <strong>{fmtMoney(t.costo_total)}</strong></span>
                    </div>
                    {t.motivo && <p className="text-[11px] text-gray-500 mt-1 italic">"{t.motivo}"</p>}
                  </div>
                  <button
                    onClick={() => handleVerificar(t)}
                    className="px-3 py-2 bg-emerald-600 text-white rounded text-sm font-medium hover:bg-emerald-700 flex items-center gap-1.5"
                    data-testid={`btn-verificar-${t.codigo}`}
                  >
                    <Check className="h-4 w-4" /> Verificar
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TODAS */}
      {tab === 'todas' && (
        <div>
          <div className="flex flex-wrap gap-2 mb-3 items-end">
            <div>
              <label className="text-xs text-gray-500">Estado</label>
              <select
                value={filtros.estado}
                onChange={(e) => setFiltros(p => ({ ...p, estado: e.target.value }))}
                className="border rounded px-2 py-1 text-sm h-8"
              >
                <option value="">Todos</option>
                <option value="CONFIRMADO">Confirmado</option>
                <option value="REVERSADA">Reversada</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Desde</label>
              <input type="date" value={filtros.fecha_desde}
                onChange={(e) => setFiltros(p => ({ ...p, fecha_desde: e.target.value }))}
                className="border rounded px-2 py-1 text-sm h-8" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Hasta</label>
              <input type="date" value={filtros.fecha_hasta}
                onChange={(e) => setFiltros(p => ({ ...p, fecha_hasta: e.target.value }))}
                className="border rounded px-2 py-1 text-sm h-8" />
            </div>
          </div>

          <div className="rounded-lg border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2">Código</th>
                  <th className="text-left px-3 py-2">Fecha</th>
                  <th className="text-left px-3 py-2">Origen → Destino</th>
                  <th className="text-left px-3 py-2">Item</th>
                  <th className="text-right px-3 py-2">Cantidad</th>
                  <th className="text-right px-3 py-2">Costo total</th>
                  <th className="text-center px-3 py-2">Estado</th>
                  <th className="text-center px-3 py-2">Verificada</th>
                  <th className="text-center px-3 py-2 w-24">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="text-center py-8 text-gray-400">Cargando...</td></tr>
                ) : todas.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-8 text-gray-400">Sin transferencias</td></tr>
                ) : todas.map(t => (
                  <tr key={t.transferencia_prod_id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">{t.codigo}</td>
                    <td className="px-3 py-2 text-xs">{fmtDate(t.fecha)}</td>
                    <td className="px-3 py-2 text-xs">
                      <div>{t.linea_origen_nombre}</div>
                      <div className="text-gray-400">→ {t.linea_destino_nombre}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-mono">{t.item_origen_codigo}</div>
                      <div className="text-gray-500">{t.item_origen_nombre}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {fmtNumber(t.cantidad)} <span className="text-gray-400">{t.unidad_medida}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-sm font-semibold">
                      {fmtMoney(t.costo_total)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${ESTADO_BADGE[t.estado] || ''}`}>
                        {t.estado}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {t.verificada ? (
                        <span className="text-emerald-600 inline-flex items-center gap-0.5 text-xs">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {t.verificada_por && <span className="text-[10px] text-gray-500">{t.verificada_por}</span>}
                        </span>
                      ) : t.estado === 'CONFIRMADO' ? (
                        <span className="text-amber-600 inline-flex items-center gap-0.5 text-xs">
                          <AlertCircle className="h-3.5 w-3.5" /> Pendiente
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {t.estado === 'CONFIRMADO' && !t.verificada && (
                        <button
                          onClick={() => handleVerificar(t)}
                          className="text-emerald-600 hover:bg-emerald-50 rounded p-1"
                          title="Verificar"
                          data-testid={`btn-row-verif-${t.codigo}`}
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      )}
                      {t.verificada && (
                        <button
                          onClick={() => handleAnularVerif(t)}
                          className="text-orange-500 hover:bg-orange-50 rounded p-1"
                          title="Anular verificación"
                          data-testid={`btn-row-anular-${t.codigo}`}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* RESUMEN */}
      {tab === 'resumen' && (
        <div>
          <div className="flex flex-wrap gap-2 mb-3 items-end">
            <div>
              <label className="text-xs text-gray-500">Desde</label>
              <input type="date" value={filtros.fecha_desde}
                onChange={(e) => setFiltros(p => ({ ...p, fecha_desde: e.target.value }))}
                className="border rounded px-2 py-1 text-sm h-8" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Hasta</label>
              <input type="date" value={filtros.fecha_hasta}
                onChange={(e) => setFiltros(p => ({ ...p, fecha_hasta: e.target.value }))}
                className="border rounded px-2 py-1 text-sm h-8" />
            </div>
            <label className="text-xs flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={filtros.incluir_no_verificadas}
                onChange={(e) => setFiltros(p => ({ ...p, incluir_no_verificadas: e.target.checked }))}
              />
              Incluir no verificadas
            </label>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-400">Cargando...</div>
          ) : !resumen ? null : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <div className="rounded-lg border p-3 bg-blue-50">
                  <div className="text-xs uppercase tracking-wider text-blue-700">Total enviado</div>
                  <div className="text-xl font-bold font-mono mt-1">{fmtMoney(resumen.totales.enviado)}</div>
                </div>
                <div className="rounded-lg border p-3 bg-emerald-50">
                  <div className="text-xs uppercase tracking-wider text-emerald-700">Total recibido</div>
                  <div className="text-xl font-bold font-mono mt-1">{fmtMoney(resumen.totales.recibido)}</div>
                </div>
                <div className="rounded-lg border p-3 bg-gray-50">
                  <div className="text-xs uppercase tracking-wider text-gray-700">Neto empresa</div>
                  <div className="text-xl font-bold font-mono mt-1">{fmtMoney(resumen.totales.neto_empresa)}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">debería ser 0 (se compensa)</div>
                </div>
              </div>

              <div className="rounded-lg border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2">Línea</th>
                      <th className="text-right px-3 py-2">Enviadas</th>
                      <th className="text-right px-3 py-2">Total enviado</th>
                      <th className="text-right px-3 py-2">Recibidas</th>
                      <th className="text-right px-3 py-2">Total recibido</th>
                      <th className="text-right px-3 py-2">Neto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resumen.data.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-6 text-gray-400">Sin movimientos en el rango</td></tr>
                    ) : resumen.data.map(r => (
                      <tr key={r.linea_id} className="border-t">
                        <td className="px-3 py-2 font-medium">{r.linea}</td>
                        <td className="px-3 py-2 text-right text-xs text-gray-500">{r.cant_enviadas}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.total_enviado)}</td>
                        <td className="px-3 py-2 text-right text-xs text-gray-500">{r.cant_recibidas}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.total_recibido)}</td>
                        <td className={`px-3 py-2 text-right font-mono font-semibold ${r.neto > 0 ? 'text-emerald-600' : r.neto < 0 ? 'text-red-600' : ''}`}>
                          {r.neto > 0 ? '+' : ''}{fmtMoney(r.neto)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-500 mt-2">
                Período: {fmtDate(resumen.rango.desde)} — {fmtDate(resumen.rango.hasta)}
                {resumen.incluir_no_verificadas && <span> · Incluye no verificadas</span>}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
